// /src/components/MaintenanceGate.tsx

// What? Role-aware maintenance gate.
// Why? To take the application offline for everyone except Metalmind Admins
//      while maintenance mode is on, without blocking the login flow.
// How?
// - Rendered post-authentication, so the login flow always stays open while
//   maintenance mode is on.
// - When maintenance mode is off it is inert: children render immediately with
//   no extra work (behavior unchanged).
// - When maintenance mode is on it fetches the authenticated user's profile
//   (a GET, which passes an API write-block) and admits only Metalmind Admins;
//   everyone else sees the MaintenancePage.
// - A profile-fetch or auth failure degrades to the MaintenancePage rather than
//   an error screen or crash.

import React from "react";
import PageLoading from "./PageLoading";
import MaintenancePage from "../views/MaintenancePage";
import { useReadUser } from "../services/api";
import { getRuntimeConfig, isRuntimeFlagEnabled } from "../config/runtimeConfig";

interface MaintenanceGateProps {
  children: React.ReactNode;
}

const MaintenanceGate: React.FC<MaintenanceGateProps> = ({ children }) => {
  const maintenanceModeEnabled = isRuntimeFlagEnabled(
    getRuntimeConfig().maintenanceMode,
  );

  if (!maintenanceModeEnabled) {
    return <>{children}</>;
  }

  return <MaintenanceRoleGate>{children}</MaintenanceRoleGate>;
};

// Split out from MaintenanceGate so its hooks never run when maintenance mode
// is off (keeping the flag-off path free of any profile fetch).
const MaintenanceRoleGate: React.FC<MaintenanceGateProps> = ({ children }) => {
  const readUser = useReadUser();
  const [decision, setDecision] = React.useState<
    "checking" | "admin" | "blocked"
  >("checking");

  React.useEffect(() => {
    let active = true;
    readUser()
      .then((user) => {
        if (active) {
          setDecision(user?.is_metalmind_admin === true ? "admin" : "blocked");
        }
      })
      .catch(() => {
        if (active) setDecision("blocked");
      });
    return () => {
      active = false;
    };
  }, [readUser]);

  if (decision === "checking") {
    return <PageLoading />;
  }
  if (decision === "blocked") {
    return <MaintenancePage />;
  }
  return <>{children}</>;
};

export default MaintenanceGate;
